// Propósito: editar horarios de abertura/fechamento da feira atual
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import useCurrentFairWindow from "../../services/useCurrentFairWindow";
import useUpdateCurrentFairWindow from "../../services/useUpdateCurrentFairWindow";
import useCancelFair from "../../services/useCancelFair";
import useUncancelFair from "../../services/useUncancelFair";

const STATUS: Record<string, string> = {
  scheduled: "agendada",
  open: "aberta",
  closed: "fechada",
  delivered: "entregue",
  cancelled: "cancelada",
};

const FairPage = () => {
  const { data, isLoading } = useCurrentFairWindow();
  const { mutate } = useUpdateCurrentFairWindow();
  const { mutate: cancelFair } = useCancelFair();
  const { mutate: uncancelFair } = useUncancelFair();

  // estados locais para edição
  const [openAt, setOpenAt] = useState<Dayjs | null>(null);
  const [closeAt, setCloseAt] = useState<Dayjs | null>(null);

  // feedback UI
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  // sincroniza estado local quando a API refetch/atualiza
  useEffect(() => {
    if (data) {
      setOpenAt(dayjs(data.open_at));
      setCloseAt(dayjs(data.close_at));
    }
  }, [data]);

  // validações básicas
  const openValid = !!openAt && openAt.isValid();
  const closeValid = !!closeAt && closeAt.isValid();
  const orderValid = openValid && closeValid && openAt!.isBefore(closeAt!);

  // detecta alterações para habilitar/desabilitar "Salvar"
  const isDirty = useMemo(() => {
    if (!openValid || !closeValid || !data) return false;
    const serverOpenISO = dayjs(data.open_at).toISOString();
    const serverCloseISO = dayjs(data.close_at).toISOString();
    const localOpenISO = openAt!.toISOString();
    const localCloseISO = closeAt!.toISOString();
    return serverOpenISO !== localOpenISO || serverCloseISO !== localCloseISO;
  }, [data, openAt, closeAt, openValid, closeValid]);

  if (isLoading) return null;
  if (!data) return null;

  function handleSave() {
    if (!orderValid) return;
    setSaving(true);
    mutate(
      {
        // envia em ISO-8601; DRF lida com timezone (converte para UTC)
        openAt: openAt!.toISOString(),
        closeAt: closeAt!.toISOString(),
      },
      {
        onSuccess: () => {
          setToast({ type: "success", msg: "Janela atualizada com sucesso." });
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.detail ||
            err?.message ||
            "Falha ao atualizar a janela da feira.";
          setToast({ type: "error", msg });
        },
        onSettled: () => setSaving(false),
      },
    );
  }

  function handleReset() {
    if (!data) {
      return;
    }

    setOpenAt(dayjs(data.open_at));
    setCloseAt(dayjs(data.close_at));
  }

  function handleCancel() {
    if (!data) return;

    const confirmed = window.confirm(
      "Tem certeza que deseja cancelar esta feira? Os pedidos já feitos não serão afetados.",
    );

    if (!confirmed) return;

    setCancelling(true);
    cancelFair(undefined, {
      onSuccess: () => {
        setToast({ type: "success", msg: "Feira cancelada com sucesso." });
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Falha ao cancelar a feira.";
        setToast({ type: "error", msg });
      },
      onSettled: () => setCancelling(false),
    });
  }

  function handleUncancel() {
    if (!data) return;

    setCancelling(true);
    uncancelFair(undefined, {
      onSuccess: () => {
        setToast({ type: "success", msg: "Feira descancelada com sucesso." });
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Falha ao descancelar a feira.";
        setToast({ type: "error", msg });
      },
      onSettled: () => setCancelling(false),
    });
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          margin: "2rem auto",
          maxWidth: "900px",
          p: "0 1rem",
        }}
      >
        <Typography variant="h4" component="h1">
          Feira do dia{" "}
          {data.delivery_at
            ? dayjs(data.delivery_at).format("DD/MM/YYYY")
            : dayjs(data.open_at).format("DD/MM/YYYY")}{" "}
          (#{data.id})
        </Typography>
        <Typography variant="h5" component="h2">
          Status: <strong>{STATUS[data.status]}</strong>
        </Typography>

        {/* Valores atuais (só leitura) */}
        <Box>
          Horário de abertura: {dayjs(data.open_at).format("DD/MM/YYYY HH:mm")}
        </Box>
        <Box>
          Horário de fechamento:{" "}
          {dayjs(data.close_at).format("DD/MM/YYYY HH:mm")}
        </Box>

        {/* Editores */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-start"
          sx={{ mt: 2 }}
        >
          <DateTimePicker
            ampm={false}
            disabled={data.status === "cancelled"}
            label="Nova abertura"
            value={openAt}
            onChange={setOpenAt}
            slotProps={{
              textField: {
                fullWidth: true,
                error: openValid && closeValid ? !orderValid : false,
                helperText:
                  openValid && closeValid && !orderValid
                    ? "A abertura deve ser anterior ao fechamento."
                    : " ",
              },
            }}
          />

          <DateTimePicker
            ampm={false}
            disabled={data.status === "cancelled"}
            label="Novo fechamento"
            value={closeAt}
            onChange={setCloseAt}
            // impede escolher fechamento antes da abertura
            minDateTime={openAt ?? undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                error: openValid && closeValid ? !orderValid : false,
                helperText:
                  openValid && closeValid && !orderValid
                    ? "O fechamento deve ser após a abertura."
                    : " ",
              },
            }}
          />
        </Stack>

        {/* Ações */}
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !orderValid ||
              !isDirty ||
              saving ||
              data.status === "cancelled" ||
              data.status === "delivered"
            }
          >
            {saving ? "Salvando..." : "Salvar alterações"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!isDirty || saving}
          >
            Reverter
          </Button>
        </Stack>

        {/* Divisor */}
        <Divider sx={{ mt: 3, mb: 2 }} />

        {/* Ações de Cancelamento */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Gerenciar Status da Feira
          </Typography>

          {data.status === "cancelled" ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                onClick={handleUncancel}
                disabled={cancelling}
              >
                {cancelling ? "Descancelando..." : "Descancelar Feira"}
              </Button>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                Esta feira está cancelada. Clique em "Descancelar Feira" para
                reativá-la.
              </Typography>
            </Stack>
          ) : data.status === "delivered" ? (
            <Typography variant="body2" color="text.secondary">
              Esta feira já foi entregue e não pode ser cancelada.
            </Typography>
          ) : (
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? "Cancelando..." : "Cancelar Feira"}
              </Button>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                Cancela a feira, impedindo novos pedidos. Pedidos existentes não
                serão afetados.
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Feedback */}
        <Snackbar
          open={!!toast}
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setToast(null)}
            severity={toast?.type}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast?.msg}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default FairPage;
